import {
  User, Module, Course, Cohort, SubmissionRecord, CourseStudent, GradingStatsRow,
} from '../../common/api/models';

/**
 * Maps admin object names to their TypeScript types.
 *
 * Key Convention:
 * - Simple keys (e.g., 'users', 'modules') map to base Orval types
 * - Compound keys (e.g., 'courses/users') map to context-specific extended types
 *
 * Note: The key may differ from the API endpoint. For example:
 * - Key: 'courses/users'
 * - API endpoint: '/courses/{id}/users'
 * - Type: CourseStudent (extends User with enrollment data)
 */
export interface AdminModelMap {
  users: User;
  modules: Module;
  courses: Course;
  cohorts: Cohort;
  'reports/submissions': SubmissionRecord;
  'reports/grading-stats': GradingStatsRow;
  'reports/enrollments': SubmissionRecord;
  'modules/approvals': Module & {
    requestedAt?: string;
    requestedBy?: string;
    approvalTypeLabel?: string;
  };
  /**
   * Students enrolled in a course context.
   * API endpoint: /courses/{courseId}/users
   * Extends User with course-specific fields (enrollmentDate, orderId, etc.)
   */
  'courses/users': CourseStudent;
  editAccessRequests: unknown;
  reports: unknown;
}

export type AdminObjectName = keyof AdminModelMap;

export interface ColumnDefinition<T = unknown> {
  label: string;
  width?: number;
  type: 'string' | 'int' | 'bool' | 'date' | 'list' | 'custom';
  sortable?: boolean;
  defaultSort?: 'asc' | 'desc';
  editable?: boolean;
  render?: (row: T) => React.ReactNode;
}

export type ColumnConfig = {
  [K in AdminObjectName]?: Record<string, ColumnDefinition<AdminModelMap[K]>>
  | Record<string, Record<string, ColumnDefinition<AdminModelMap[K]>>>;
} & Record<string, unknown>;

/**
 * Sortable column keys are sent as `?sort=` to the matching list endpoint.
 * Keep them in the repository `query_mapper` — the monorepo contract is
 * `api/app/tests/unit/repositories/test_admin_sort_field_contract.py`.
 */
export const columnConfig: ColumnConfig = {
  users: {
    lastname: {
      label: 'Lastname', width: 150, type: 'string', sortable: true, defaultSort: 'asc',
    },
    firstname: {
      label: 'Firstname', width: 150, type: 'string', sortable: true,
    },
    username: {
      label: 'Username', width: 150, type: 'string', sortable: true,
    },
    email: {
      label: 'Email', width: 250, type: 'string', sortable: true,
    },
    role: {
      label: 'Role', width: 100, type: 'string', sortable: true,
    },
    'cohort.title': {
      label: 'Cohort', width: 150, type: 'string', sortable: true,
    },
    state: {
      label: 'State', width: 100, type: 'string', sortable: true,
    },
    'trial.isTrial': {
      label: 'Trial', width: 50, type: 'bool', sortable: true,
    },
  },
  modules: {
    title: {
      label: 'Title', type: 'string', sortable: true, defaultSort: 'asc',
    },
    'author.name': {
      label: 'Author', width: 150, type: 'string', sortable: true,
    },
    'state.workflowState': {
      label: 'State', width: 120, type: 'string', sortable: true,
    },
    isDeprecated: {
      label: 'Deprecated', width: 60, type: 'bool', sortable: true, editable: true,
    },
  },
  'modules/approvals': {
    title: {
      label: 'Module Title', width: 300, type: 'string', sortable: true, defaultSort: 'asc',
    },
    version: {
      label: 'Version', width: 80, type: 'int', sortable: true,
    },
    'author.name': {
      label: 'Author', width: 150, type: 'string', sortable: true,
    },
    requestedAt: {
      label: 'Requested', width: 120, type: 'date', sortable: true,
    },
    approvalTypeLabel: {
      label: 'Type', width: 120, type: 'string', sortable: true,
    },
  },
  courses: {
    title: {
      label: 'Title', width: 300, type: 'string', sortable: true, defaultSort: 'asc',
    },
    description: { label: 'Description', type: 'string', sortable: true },
    language: {
      label: 'Language', width: 80, type: 'string', sortable: true,
    },
    ects: {
      label: 'ECTS Points', width: 120, type: 'string', sortable: true,
    },
    state: {
      label: 'State', width: 120, type: 'string', sortable: true,
    },
    isDeprecated: {
      label: 'Depr.', width: 60, type: 'bool', sortable: true, editable: true,
    },
  },
  cohorts: {
    title: {
      label: 'Title', width: 200, type: 'string', sortable: true, defaultSort: 'asc',
    },
    state: {
      label: 'State', width: 100, type: 'string', sortable: true,
    },
    isDeprecated: {
      label: 'Depr.', width: 60, type: 'bool', sortable: true, editable: true,
    },
  },
  editAccessRequests: {
    createdOn: {
      label: 'Date of Request', width: 120, type: 'date', sortable: false,
    },
    moduleTitle: {
      label: 'Module', width: 300, type: 'string', sortable: false,
    },
    authorName: {
      label: 'Module Author', width: 200, type: 'string', sortable: false,
    },
  },
  reports: {
    submissions: {
      correctionDate: {
        label: 'Graded', width: 85, type: 'date', sortable: true,
      },
      correctionDuedate: {
        label: 'Grading Due', width: 85, type: 'date', sortable: false,
      },
      'course.title': {
        label: 'Course', width: 300, type: 'string', sortable: true,
      },
      'module.title': {
        label: 'Module', width: 300, type: 'string', sortable: true,
      },
      student: {
        label: 'Student',
        width: 120,
        type: 'string',
        sortable: true,
        defaultSort: 'asc',
        render: (row: SubmissionRecord) => {
          const lastname = row.student?.lastname?.trim?.() || '';
          const firstname = row.student?.firstname?.trim?.() || '';
          if (lastname && firstname) return `${lastname}, ${firstname}`;
          return lastname || firstname || row.student?.id || '';
        },
      },
      'student.firstname': {
        label: 'Firstname', width: 120, type: 'string', sortable: true,
      },
      'corrector.lastname': {
        label: 'Corrector', width: 140, type: 'string', sortable: true,
      },
      'grading.grade': {
        label: 'Grade', width: 30, type: 'string', sortable: true,
      },
      examAttempts: {
        label: 'Attempts',
        width: 80,
        type: 'string',
        sortable: false,
        render: (row: SubmissionRecord) => (
          row.examAttemptsAllowed == null
            ? ''
            : `${row.examAttemptsUsed ?? 0} / ${row.examAttemptsAllowed}`
        ),
      },
      state: {
        label: 'State', width: 100, type: 'string', sortable: true,
      },
    },
    gradingStats: {
      lecturerName: {
        label: 'Lecturer', width: 220, type: 'string', sortable: true, defaultSort: 'asc',
      },
      month: {
        label: 'Month', width: 100, type: 'string', sortable: true,
      },
      total: {
        label: 'Total', width: 80, type: 'int', sortable: true,
      },
      withOralExam: {
        label: 'With Oral Exam', width: 120, type: 'int', sortable: true,
      },
      withoutOralExam: {
        label: 'Without Oral Exam', width: 140, type: 'int', sortable: true,
      },
    },
    enrollments: {
      correctionDate: {
        label: 'Correction Date', width: 90, type: 'date', sortable: true,
      },
      correctionDuedate: {
        label: 'Correction Due', width: 90, type: 'date', sortable: false,
      },
      'course.title': {
        label: 'Course', width: 350, type: 'string', sortable: true,
      },
      'student.cohort.title': {
        label: 'Cohort', width: 350, type: 'string', sortable: true,
      },
      'module.title': {
        label: 'Module', width: 350, type: 'string', sortable: true,
      },
      'student.lastname': {
        label: 'Student', width: 100, type: 'string', sortable: true,
      },
      'student.firstname': {
        label: 'Firstname', width: 100, type: 'string', sortable: true,
      },
      'corrector.lastname': {
        label: 'Corrector', width: 100, type: 'string', sortable: true,
      },
      'grading.grade': {
        label: 'Grade', width: 50, type: 'string', sortable: true,
      },
    },
  },
};

export interface SearchConfigEntry {
  defaultSearchField: string;
  searchableFields: string[];
}

export const searchConfig: Record<string, SearchConfigEntry> = {
  users: {
    defaultSearchField: 'lastname',
    searchableFields: ['lastname', 'firstname', 'email', 'username', 'role', 'state', 'trial', 'title'],
  },
  modules: {
    defaultSearchField: 'title',
    searchableFields: ['title', 'author', 'description', 'state'],
  },
  'modules/approvals': {
    defaultSearchField: 'title',
    searchableFields: ['title', 'author.name'],
  },
  courses: {
    defaultSearchField: 'title',
    searchableFields: ['title', 'description', 'state'],
  },
  cohorts: {
    defaultSearchField: 'title',
    searchableFields: ['title', 'state'],
  },
  'reports/submissions': {
    defaultSearchField: 'student',
    searchableFields: ['student', 'course', 'module', 'corrector', 'grade'],
  },
  'reports/grading-stats': {
    defaultSearchField: 'lecturerName',
    searchableFields: ['lecturerName', 'month'],
  },
  'reports/enrollments': {
    defaultSearchField: 'student.lastname',
    searchableFields: [
      'student.lastname',
      'student.firstname',
      'course.title',
      'module.title',
      'corrector.lastname',
      'student.cohort.title',
      'grading.grade',
    ],
  },
  'courses/users': {
    defaultSearchField: 'lastname',
    searchableFields: ['lastname', 'firstname', 'email', 'username', 'id'],
  },
};

export interface StateDefinition {
  id: string;
  label: string;
}

export interface LifecycleConfigEntry {
  states?: StateDefinition[];
  defaultStates?: string[];
  supportsDeprecation: boolean;
}

export const lifecycleConfig: Record<string, LifecycleConfigEntry> = {
  users: {
    states: [
      { id: 'active', label: 'Active' },
      { id: 'stale', label: 'Stale' },
      { id: 'finished', label: 'Finished' },
      { id: 'inactive', label: 'Inactive' },
    ],
    defaultStates: ['active'],
    supportsDeprecation: false,
  },
  courses: {
    states: [
      { id: 'active', label: 'Active' },
      { id: 'stale', label: 'Stale' },
      { id: 'retired', label: 'Retired' },
    ],
    defaultStates: ['active'],
    supportsDeprecation: true,
  },
  cohorts: {
    states: [
      { id: 'active', label: 'Active' },
      { id: 'stale', label: 'Stale' },
      { id: 'retired', label: 'Retired' },
    ],
    defaultStates: ['active'],
    supportsDeprecation: true,
  },
  modules: {
    states: [
      { id: 'draft', label: 'Draft' },
      { id: 'pending_approval', label: 'Pending' },
      { id: 'published', label: 'Published' },
      { id: 'retired', label: 'Retired' },
    ],
    defaultStates: ['draft', 'published'],
    supportsDeprecation: true,
  },
};
